import React, { useState } from 'react';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaUser, FaPaperPlane, FaCommentDots } from 'react-icons/fa';

const ContactUs = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Name field: only allow letters and spaces
    if (name === 'name') {
      const cleaned = value.replace(/[^a-zA-Z\s]/g, '');
      setForm({ ...form, name: cleaned });
      setErrors((prev) => ({ ...prev, name: cleaned.trim().length < 2 && cleaned.length > 0 ? 'Name must be at least 2 characters' : '' }));
      return;
    }
    setForm({ ...form, [name]: value });
    // Clear error on change
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = { name: '', email: '', message: '' };
    let valid = true;

    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
      valid = false;
    } else if (form.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
      valid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(form.name.trim())) {
      newErrors.name = 'Name must contain only letters';
      valid = false;
    }

    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!emailRegex.test(form.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
      valid = false;
    }

    if (!form.message.trim()) {
      newErrors.message = 'Message is required';
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitted(true);
    setForm({ name: '', email: '', message: '' });
    setErrors({ name: '', email: '', message: '' });
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-cyan-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header section */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-8 text-white">
            <h1 className="text-4xl font-bold mb-3">Contact Us</h1>
            <p className="text-green-100 text-xl">Have a question or feedback? Fill the form and we'll get back to you.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 p-8">
            <div className="space-y-6">
              {submitted && (
                <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
                  <FaPaperPlane className="shrink-0 text-green-500" />
                  Message sent! We'll get back to you within 24 hours.
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Name */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
                    <FaUser className="text-green-600" /> Name
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className={`w-full border rounded-xl p-4 focus:ring-2 focus:border-transparent transition-all ${errors.name ? 'border-red-400 focus:ring-red-400 bg-red-50' : 'border-gray-300 focus:ring-green-500'}`}
                    placeholder="Enter your name (letters only)"
                  />
                  {errors.name && <p className="mt-1.5 text-xs font-semibold text-red-500">{errors.name}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
                    <FaEnvelope className="text-green-600" /> Email
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className={`w-full border rounded-xl p-4 focus:ring-2 focus:border-transparent transition-all ${errors.email ? 'border-red-400 focus:ring-red-400 bg-red-50' : 'border-gray-300 focus:ring-green-500'}`}
                    placeholder="Enter your email address"
                  />
                  {errors.email && <p className="mt-1.5 text-xs font-semibold text-red-500">{errors.email}</p>}
                </div>

                {/* Message */}
                <div>
                  <label className="block text-gray-700 font-medium mb-2 flex items-center gap-2">
                    <FaCommentDots className="text-green-600" /> Message
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    className={`w-full border rounded-xl p-4 h-40 focus:ring-2 focus:border-transparent transition-all resize-none ${errors.message ? 'border-red-400 focus:ring-red-400 bg-red-50' : 'border-gray-300 focus:ring-green-500'}`}
                    placeholder="Type your message here..."
                  />
                  {errors.message && <p className="mt-1.5 text-xs font-semibold text-red-500">{errors.message}</p>}
                </div>

                <div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] shadow-lg"
                  >
                    <FaPaperPlane /> Send Message
                  </button>
                </div>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Contact Information</h3>
                
                <div className="space-y-5">
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                    <div className="bg-green-100 p-3 rounded-lg text-green-700">
                      <FaEnvelope size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">Customer Care</h4>
                      <p className="text-gray-600">info@kisanfresh.com</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                    <div className="bg-green-100 p-3 rounded-lg text-green-700">
                      <FaPhone size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">Call Us</h4>
                      <p className="text-gray-600">+91 9356700237</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm">
                    <div className="bg-green-100 p-3 rounded-lg text-green-700">
                      <FaMapMarkerAlt size={20} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-800">Visit Us</h4>
                      <p className="text-gray-600">Pune, Maharashtra, India</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-600 to-green-700 rounded-2xl p-6 text-white">
                <h3 className="text-xl font-bold mb-3">Business Hours</h3>
                <p className="mb-2">Monday - Friday: 9AM - 8PM</p>
                <p>Saturday: 10AM - 6PM</p>
                <p className="mt-3 text-emerald-100 italic">We respond to all inquiries within 24 hours</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;